function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};
    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }
  return target;
}
function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
import React, { useMemo } from "react";
import PropTypes from "prop-types";
import useTheme from "../hooks/useTheme";
import useListType, { ListTypeProvider } from "../hooks/useListType";
import useTextStyle, { TextStyleProvider } from "../hooks/useTextStyle";
import { responsiveMarginType, responsivePropType } from "../hooks/useResponsiveProp";
import useResponsivePropsCSS from "../hooks/useResponsivePropsCSS";
import { mergeProps } from "../utils/component";
import { responsiveMargin } from "../utils/css";
import { jsx as ___EmotionJSX } from "@emotion/core";
var TYPES = ["unordered", "ordered", "steps"];
var VARIANTS = ["default", "danger"];
var TEXT_STYLES = ["subtitle1", "subtitle2", "body1", "body2"];
var DEFAULT_PROPS = {
  type: "unordered",
  variant: "default",
  textStyle: "body1"
};
List.TYPES = TYPES;
List.VARIANTS = VARIANTS;
List.TEXT_STYLES = TEXT_STYLES;
List.DEFAULT_PROPS = DEFAULT_PROPS;
function Item(_ref) {
  var children = _ref.children,
    testId = _ref.testId;
  var theme = useTheme();
  var _useListType = useListType(),
    type = _useListType.type,
    variant = _useListType.variant;
  var _useTextStyle = useTextStyle(),
    textStyle = _useTextStyle.textStyle;
  return ___EmotionJSX("li", {
    css: theme.list.getCSS({
      targetElement: "item",
      type: type,
      variant: variant,
      textStyle: textStyle
    }),
    "data-testid": testId
  }, children);
}
Item.propTypes = {
  children: PropTypes.node.isRequired,
  testId: PropTypes.string
};
function List(props) {
  var _useListType2 = useListType(),
    inheritedListType = _useListType2.type,
    inheritedVariantType = _useListType2.variant;
  var _useTextStyle2 = useTextStyle(),
    inheritedTextStyle = _useTextStyle2.textStyle;
  var inheritedProps = {
    type: inheritedListType,
    variant: inheritedVariantType,
    textStyle: inheritedTextStyle
  };
  var mergedProps = mergeProps(props, DEFAULT_PROPS, inheritedProps, {
    type: function type(_type) {
      return TYPES.includes(_type);
    },
    variant: function variant(_variant) {
      return VARIANTS.includes(_variant);
    },
    textStyle: function textStyle(_textStyle) {
      return TEXT_STYLES.includes(_textStyle);
    }
  });
  var type = mergedProps.type,
    variant = mergedProps.variant,
    textStyle = mergedProps.textStyle,
    children = mergedProps.children,
    testId = mergedProps.testId;
  var listInfo = useMemo(function () {
    return {
      type: type,
      variant: variant
    };
  }, [type, variant]);
  var listCSS = useResponsivePropsCSS(mergedProps, DEFAULT_PROPS, {
    textStyle: function textStyle(_ref2, theme) {
      var _textStyle2 = _ref2.textStyle;
      return theme.list.getCSS({
        targetElement: "list",
        type: type,
        textStyle: _textStyle2
      });
    },
    margin: responsiveMargin
  });
  var ListComponent = type === "unordered" ? "ul" : "ol";
  var items = React.Children.toArray(children).filter(
  // Ignore all children that aren't List.Item
  function (child) {
    return child.type === Item;
  });
  var list = ___EmotionJSX(ListComponent, {
    css: listCSS,
    "data-testid": testId
  }, items);
  if (textStyle) {
    list = ___EmotionJSX(TextStyleProvider, {
      value: textStyle
    }, list);
  }
  return ___EmotionJSX(ListTypeProvider, {
    value: listInfo
  }, list);
}
List.propTypes = _objectSpread(_objectSpread(_objectSpread({}, responsiveMarginType), responsivePropType("textStyle", PropTypes.oneOf(TEXT_STYLES))), {}, {
  type: PropTypes.oneOf(TYPES),
  variant: PropTypes.oneOf(VARIANTS),
  children: PropTypes.node.isRequired,
  testId: PropTypes.string
});
List.Item = Item;
export default List;